/**
 * The Påminnelser modal: the schedule editor for admins on an open order (a
 * read-only summary otherwise) and every reminder the job has sent, so an
 * admin can check what went out.
 */
import React from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import ReminderFields from './ReminderFields';
import type { WorkOrder } from '../../types/workOrders';
import {
    REMINDER_KIND_LABELS,
    ReminderDraft,
    describeReminderTiming,
    parseReminderLog,
    scheduleTiming,
} from '../../types/workOrderReminders';
import { formatDateForDisplay } from '../../Helpers/DateUtils';
import './WorkOrderReminders.css';

interface WorkOrderRemindersModalProps {
    show: boolean;
    workOrder: Pick<WorkOrder, 'key' | 'dueDate' | 'reminders'>;
    canEdit: boolean;
    nameOf: (uid: string) => string;
    draft: ReminderDraft | null;
    onDraftChange: (draft: ReminderDraft) => void;
    saving: boolean;
    error: string | null;
    onSave: () => void;
    onHide: () => void;
}

const WorkOrderRemindersModal: React.FC<WorkOrderRemindersModalProps> = ({
    show, workOrder, canEdit, nameOf, draft, onDraftChange, saving, error, onSave, onHide,
}) => {
    const timing = scheduleTiming(workOrder.reminders?.schedule);
    const log = parseReminderLog(workOrder.reminders?.sent);
    const names = (uids: string[]) => uids.map(nameOf).join(', ');
    const editable = canEdit && draft !== null;

    return (
        <Modal show={show} onHide={() => { if (!saving) onHide(); }} centered>
            <Modal.Header closeButton>
                <Modal.Title>Påminnelser</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {editable ? (
                    <ReminderFields
                        draft={draft}
                        onChange={onDraftChange}
                        idPrefix={`wo-reminders-${workOrder.key}`}
                        disabled={saving}
                    />
                ) : (
                    <p className="mb-0">{describeReminderTiming(timing)}</p>
                )}
                {!workOrder.dueDate && (
                    <Alert variant="warning" className="mt-3 mb-0">
                        Ärendet saknar förfallodatum – inga påminnelser skickas förrän ett datum är satt.
                    </Alert>
                )}

                <h3 className="h6 mt-4">Skickade påminnelser</h3>
                {log.length === 0 ? (
                    <p className="text-muted small mb-0">Inga påminnelser har skickats ännu.</p>
                ) : (
                    <ul className="reminder-log">
                        {log.map((row) => (
                            <li key={row.key}>
                                <span className="text-muted">{formatDateForDisplay(row.date)}</span>{' '}
                                {REMINDER_KIND_LABELS[row.kind]}
                                {row.recipients.length > 0 && <> till {names(row.recipients)}</>}
                                {row.skipped.length > 0 && (
                                    <span className="text-warning"> · ej skickat till {names(row.skipped)}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    {editable ? 'Avbryt' : 'Stäng'}
                </Button>
                {editable && (
                    <Button variant="primary" onClick={onSave} disabled={saving}>
                        {saving ? <Spinner animation="border" size="sm" /> : 'Spara'}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default WorkOrderRemindersModal;
